import { PassportInfo } from "../models/Client";
import { Client, Customer } from "./ClientInterface";
import { AssessmentSymptom, Medicine, IVitals } from "./AssessmentInterfaces";
import { BedAssignment } from "./BedAssignmentInterface";
import { IpdStatus } from "../constants/ipd";
import { TWardType } from "./WardInterface";

export type TIpdStatus = (typeof IpdStatus)[keyof typeof IpdStatus];

export interface IpdPatientRecord {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  phoneNumber: string;
}

export enum TriagePriority {
  Critical = "Critical",
  Urgent = "Urgent",
  Normal = "Normal"
}

export enum IpdTypes {
  emergency = "Emergency",
  maternity = "Maternity",
  regular = "Regular"
}

export type TIpdCase = TWardType;

export interface IpdRecord {
  referredBy: string;
  provisionalDiagnosis: string;
  hopi: string;
  admissionCondition: string;
  case?: TIpdCase;
  progressRecord?: Array<ProgressRecord>;
  authorizedPersonnelInfo: AuthorizedPersonnelInfo;
  doctorInCharge: DoctorInCharge;
  dischargeSummary?: DischargeSummary | MaternityDischargeSummary;
  patientRelatedInformation: IpdPatientRecord;
}

export interface IPD {
  id: number;
  ipdNumber: number;
  dateOfAdmission: Date;
  dateOfDischarge: Date;
  referenceNumber: string;
  claimCode: string;
  // These are not needed anymore
  // bedNumber: string;
  // wardNumber: string;
  // roomType: string;
  status: TIpdStatus;
  patientFullName?: string;
  prescribedMedications: Array<MedicationSummary>;
  clientId: number;
  expectedLengthOfStay: string;
  finalDiagnosis: string;
  emergencyContact: EmergencyContact;
  ipdRecord: IpdRecord;
  resourceCentreId: number;

  // Commented in favour of BedAssignments
  // bedId: number;
  // roomId: number;
  // wardId: number;
  active: boolean;
  bedName: string;
  authorizedPersonnelId: number;
  doctorInChargeId: number;
  triagePriority: TriagePriority | null;
  created_at: string;
  updated_at: string;

  admissionCase?: TIpdCase;
  currentCase?: TIpdCase;

  // related
  client?: Client;
  customer?: Customer;
  doctorInCharge?: DoctorInCharge;
  authorizedPersonnelInfo?: AuthorizedPersonnelInfo;
  bedAssignments?: Array<BedAssignment>;
}

export interface ProgressResult {
  cured: string;
  recovered: string;
  unchanged: string;
  worsened: string;
  lettAgainstMedicalAdvice: string;
  expired: string;
  broughtDead: string;
  absconded: string;
}

export interface IMedicine {
  brand: string;
  code: string;
  // eslint-disable-next-line camelcase
  created_at: string;
  form: string;
  genericCode: string;
  genericId: number;
  genericName: string;
  id: number;
  source: string;
  strength: string;
  isFromStock?: boolean;
}

export interface ProgressRecord {
  diagnosis?: Diagnosis;
  examination?: string;
  treatmentProvided?: string;
  operationSummary?: string;
  medicationSummary?: Array<IMedicine>;
  id: number;
  vitals?: Array<IProgressRecordVital>;
  remarks?: string;
  diet?: string;
  allergy?: string;
  symptoms?: AssessmentSymptom[];
  pastHistoryOfAllergy?: string;
  investigation?: string;
  treatmentPlan?: string;
  treatmentGiven?: string;
  tests?: Array<{ code: string; name: string }>;
  medication?: Medicine[];
  observation?: string;
  provisionalDiagnosis?: string;
  finalDiagnosis?: string;
  consultationNote?: string;
  advice?: string;
  gcs?: string;
  emergencyAssessmentVitals?: IVitals[];
  selectedDoctorIds?: number[];
  selectedDoctors?: Array<{
    id: number;
    title?: string;
    firstName?: string;
    lastName?: string;
    fullname?: string;
  }>;
}

export interface ProgressRecordVital {
  reading: string;
  name: string;
  unit: string;
}

export interface DischargeSummary {
  id: number;
  ipdCase: string;
  conditionDuringDischarge: string;
  instructionsOnDischarge: string;
  progressResult: string;
  remarks: string;
  finalDiagnosis: { diagnosis: string };
  authorizedPersonnelId: number;
  doctorInChargeId: number;
  dischargePrintTitle?: string;
  provisionalDiagnosis: string;
  hopi: string;
  prescribedMedicineOnDischarge?: MedicationSummary[] | Medicine[];
}

export interface MaternityDischargeSummary extends DischargeSummary, MaternityType {}

export interface EmergencyContact {
  name: string;
  phone: string;
  relation: string;
}

export enum PrescriptionContext {
  IPD = "IPD",
  DISCHARGE = "DISCHARGE"
}

export interface MedicationSummary extends IMedicine {
  id: number;
  prescriptionContext: PrescriptionContext;
}

export interface MaternityType {
  procedures: string;
  dateOfDelivery: string;
  weightOfBaby: string;
}

export interface AuthorizedPersonnelInfo {
  title: string;
  firstName: string;
  lastName: string;
}

export interface DoctorInCharge {
  title: string;
  firstName: string;
  lastName: string;
}

export interface IpdObjectClient {
  id: number;
  firstName: string;
  lastName: string;
  phone: string;
  active: boolean;
  email: string;
  sendEmail: boolean;
  dob: string;
  gender: string;
  registrationNo: string;
  ipdNo: string;
  passportInfo: PassportInfo;
  address: string;
  displayName: string;
  nationality: string;
}

export type IProgressRecordVital = Pick<IVitals, "reading" | "name" | "unit" | "extraInfo">;

export interface Diagnosis {
  diagnosis?: string;
  icdCode: string;
}

export interface IpdStats {
  admitted: number;
  discharged: number;
}

export interface ExtendedIpd extends IPD {
  bedId?: number;
  bedNumber: number | string;
  bedCode: string;
  roomCode: string;
  roomType?: string;
  wardCode: string;
  wardName?: string;
  patientFullName: string;
}
